import React, { FC, useMemo } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'utils/format';
import { ScheduledStep, ScheduleStatus } from 'models/production-planning';

type PausedOrdersSectionProps = {
    pausedSteps: ScheduledStep[];
};

// A paused order whose current step is not in its plan has no PAUSED step; its row
// stands for the order only, so its step, department and deadline are not shown (DD-3605).
const isPausedStep = (step: ScheduledStep): boolean => step.scheduleStatus === ScheduleStatus.PAUSED;

const comparePausedSteps = (first: ScheduledStep, second: ScheduledStep): number => {
    if (isPausedStep(first) !== isPausedStep(second)) return isPausedStep(first) ? -1 : 1;
    return first.deadline.getTime() - second.deadline.getTime();
};

const PausedOrdersSection: FC<PausedOrdersSectionProps> = ({ pausedSteps }) => {
    const { t: translate } = useTranslation(['production-dashboard']);

    const sortedSteps = useMemo(() => [...pausedSteps].sort(comparePausedSteps), [pausedSteps]);

    if (sortedSteps.length === 0) return null;

    return (
        <Accordion sx={{ mb: 2 }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={0.5}>
                    <PauseCircleIcon fontSize="small" color="warning" />
                    <Typography variant="subtitle2">
                        {translate('production-dashboard:PAUSED_ORDERS')} ({sortedSteps.length})
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('production-dashboard:ORDER_ID')}</TableCell>
                            <TableCell>{translate('production-dashboard:STEP')}</TableCell>
                            <TableCell>{translate('production-dashboard:DEPARTMENT')}</TableCell>
                            <TableCell>{translate('production-dashboard:DEADLINE')}</TableCell>
                            <TableCell>{translate('production-dashboard:STATUS_PAUSED')}</TableCell>
                            <TableCell>{translate('production-dashboard:PAUSE_REASON')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedSteps.map((step) => (
                            <TableRow
                                key={step.orderId}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => window.open(`/order-details/${step.orderId}/main-view`, '_blank')}
                            >
                                <TableCell>{step.orderDisplayId || step.orderId}</TableCell>
                                <TableCell>{(isPausedStep(step) && step.stepName) || '-'}</TableCell>
                                <TableCell>{(isPausedStep(step) && step.department) || '-'}</TableCell>
                                <TableCell>
                                    {isPausedStep(step) ? formatDate(step.deadline, 'datetime') : '-'}
                                </TableCell>
                                <TableCell>
                                    {step.lastActionType === 'pause' && step.lastActionByName
                                        ? translate('production-dashboard:PAUSED_BY', { name: step.lastActionByName })
                                        : '-'}
                                </TableCell>
                                <TableCell data-test="paused-orders-section.pause-reason">
                                    {step.pauseReason || '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </AccordionDetails>
        </Accordion>
    );
};

export default PausedOrdersSection;
